import React, { useEffect, useRef, useState } from "react";
import { api, formatApiError, API, getToken } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { hasMinRole, useAuth } from "@/contexts/AuthContext";
import {
  Paperclip, FilePdf, FileXls, FileImage, FileText, File as FileIcon,
  Download, Trash, Plus, Stack, Clock,
} from "@phosphor-icons/react";

const DOC_TYPES = [
  "general", "contract", "report", "drawing", "spec",
  "invoice", "receipt", "photo", "letter", "minutes",
  "certificate", "checklist", "model", "datasheet",
];

const iconFor = (mime) => {
  if (!mime) return FileIcon;
  if (mime.includes("pdf")) return FilePdf;
  if (mime.includes("sheet") || mime.includes("excel") || mime.includes("csv")) return FileXls;
  if (mime.startsWith("image/")) return FileImage;
  if (mime.includes("text") || mime.includes("word") || mime.includes("document")) return FileText;
  return FileIcon;
};

const formatBytes = (b) => {
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / 1024 / 1024).toFixed(2)} MB`;
};

/**
 * Reusable document drawer for ANY record type.
 * Usage:  <DocumentDrawer entityType="project" entityId={p.id} title="Project documents" />
 */
export default function DocumentDrawer({
  entityType, entityId, title = "Documents", trigger,
  badge = true,
}) {
  const { user } = useAuth();
  const canUpload = hasMinRole(user, "manager");
  const canDelete = hasMinRole(user, "admin");
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [versionFor, setVersionFor] = useState(null); // {doc_group_id, title}
  const [expanded, setExpanded] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/documents?entity_type=${entityType}&entity_id=${entityId}`);
      setItems(data.items || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { if (open) load(); /* eslint-disable-next-line */ }, [open]);

  const downloadDoc = async (docId, filename) => {
    try {
      const token = getToken();
      const res = await fetch(`${API}/documents/${docId}/download`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Download failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = filename || "document";
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(e.message); }
  };

  const del = async (docId) => {
    if (!confirm("Delete this version permanently?")) return;
    try {
      await api.delete(`/documents/${docId}`);
      toast.success("Deleted");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <Button variant="outline" size="sm" data-testid={`doc-trigger-${entityType}-${entityId}`}>
            <Paperclip size={12} className="mr-1" />
            Docs{badge && items.length > 0 ? ` (${items.length})` : ""}
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="max-w-3xl max-h-[88vh] overflow-y-auto" data-testid="doc-drawer">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <Stack size={18} className="text-blue-600" /> {title}
          </DialogTitle>
          <DialogDescription>
            Linked to {entityType} · {entityId.slice(0, 8)}… · {items.length} document{items.length !== 1 ? "s" : ""}
            {items.reduce((a, b) => a + (b.version_count || 1), 0) > items.length
              ? ` · ${items.reduce((a, b) => a + (b.version_count || 1), 0)} total versions`
              : ""}
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-center justify-between">
          <p className="text-[11px] text-slate-500">Max file size 8MB · PDF/Excel/Word/images supported.</p>
          {canUpload && (
            <Button size="sm" className="btn-primary-blue" onClick={() => { setVersionFor(null); setShowUpload(true); }} data-testid="doc-upload-btn">
              <Plus size={11} className="mr-1" /> Upload
            </Button>
          )}
        </div>

        {loading ? <div className="p-6 text-center text-sm text-slate-500">Loading…</div>
          : items.length === 0 ? <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 p-10 text-center text-sm text-slate-500">No documents yet. Upload the first one.</div>
          : (
          <ul className="divide-y divide-slate-200 rounded-xl border border-slate-200 bg-white">
            {items.map((d) => {
              const Icon = iconFor(d.mime_type);
              const isOpen = expanded[d.doc_group_id];
              return (
                <li key={d.id} className="p-3" data-testid={`doc-row-${d.id}`}>
                  <div className="flex items-start gap-3">
                    <div className="rounded-lg bg-blue-50 p-2 shrink-0">
                      <Icon size={20} weight="duotone" className="text-blue-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h5 className="font-semibold text-slate-900 truncate">{d.title}</h5>
                        <span className="inline-block rounded bg-slate-100 px-1.5 py-0.5 text-[9px] font-mono-alt uppercase">{d.doc_type}</span>
                        <span className="inline-block rounded bg-emerald-50 px-1.5 py-0.5 text-[9px] font-mono-alt uppercase text-emerald-700">v{d.version}</span>
                        {d.version_count > 1 && (
                          <button onClick={() => setExpanded({ ...expanded, [d.doc_group_id]: !isOpen })}
                            className="inline-flex items-center gap-1 rounded bg-blue-50 px-1.5 py-0.5 text-[9px] font-mono-alt uppercase text-blue-700 hover:bg-blue-100" data-testid={`doc-versions-${d.doc_group_id}`}>
                            <Clock size={9} /> {d.version_count} versions
                          </button>
                        )}
                      </div>
                      {d.description && <p className="text-xs text-slate-600 mt-0.5 line-clamp-2">{d.description}</p>}
                      <div className="mt-1 text-[10px] font-mono-alt text-slate-500">
                        {d.filename} · {formatBytes(d.size_bytes)} · {d.uploaded_by} · {new Date(d.created_at).toLocaleString()}
                      </div>
                    </div>
                    <div className="flex gap-1">
                      <Button size="sm" variant="outline" className="h-8" onClick={() => downloadDoc(d.id, d.filename)} data-testid={`doc-download-${d.id}`}>
                        <Download size={11} />
                      </Button>
                      {canUpload && (
                        <Button size="sm" variant="outline" className="h-8" onClick={() => { setVersionFor({ doc_group_id: d.doc_group_id, title: d.title }); setShowUpload(true); }} data-testid={`doc-newversion-${d.id}`}>
                          + ver
                        </Button>
                      )}
                      {canDelete && (
                        <Button size="sm" variant="ghost" className="h-8" onClick={() => del(d.id)} data-testid={`doc-del-${d.id}`}>
                          <Trash size={11} />
                        </Button>
                      )}
                    </div>
                  </div>

                  {isOpen && d.versions?.length > 1 && (
                    <ul className="mt-2 ml-12 space-y-1 border-l-2 border-slate-200 pl-3">
                      {d.versions.map((v) => (
                        <li key={v.id} className="flex items-center gap-2 text-[11px]">
                          <span className="inline-block rounded bg-slate-100 px-1.5 py-0.5 font-mono-alt">v{v.version}</span>
                          <span className="text-slate-600">{v.filename}</span>
                          <span className="text-slate-400">· {formatBytes(v.size_bytes)} · {v.uploaded_by}</span>
                          <span className="text-slate-400">· {new Date(v.created_at).toLocaleDateString()}</span>
                          <Button size="sm" variant="ghost" className="h-6 ml-auto" onClick={() => downloadDoc(v.id, v.filename)}>
                            <Download size={10} />
                          </Button>
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              );
            })}
          </ul>
        )}

        {showUpload && (
          <UploadDialog
            entityType={entityType} entityId={entityId}
            versionFor={versionFor}
            onClose={() => setShowUpload(false)}
            onUploaded={() => { setShowUpload(false); load(); }}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

function UploadDialog({ entityType, entityId, versionFor, onClose, onUploaded }) {
  const fileRef = useRef(null);
  const [title, setTitle] = useState(versionFor?.title || "");
  const [docType, setDocType] = useState("general");
  const [description, setDescription] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    const file = fileRef.current?.files?.[0];
    if (!file) { toast.error("Select a file"); return; }
    if (file.size > 8 * 1024 * 1024) { toast.error("Max 8MB"); return; }
    setBusy(true);
    try {
      const form = new FormData();
      form.append("entity_type", entityType);
      form.append("entity_id", entityId);
      form.append("title", title || file.name);
      form.append("doc_type", docType);
      form.append("description", description);
      if (versionFor?.doc_group_id) form.append("doc_group_id", versionFor.doc_group_id);
      form.append("file", file);
      const token = getToken();
      const res = await fetch(`${API}/documents/upload`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: form,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Upload failed");
      }
      toast.success(versionFor ? "New version uploaded" : "Document uploaded");
      onUploaded?.();
    } catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{versionFor ? `Upload new version · ${versionFor.title}` : "Upload document"}</DialogTitle>
          <DialogDescription>Stored in the document repository, version-tracked.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3 text-sm">
          <div><Label>Title {versionFor ? "" : "*"}</Label><Input value={title} onChange={(e) => setTitle(e.target.value)} disabled={!!versionFor} data-testid="doc-title" /></div>
          {!versionFor && (
            <div>
              <Label>Type</Label>
              <Select value={docType} onValueChange={setDocType}>
                <SelectTrigger data-testid="doc-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {DOC_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <div><Label>Description</Label><Textarea rows={2} value={description} onChange={(e) => setDescription(e.target.value)} /></div>
          <div>
            <Label>File * (max 8MB)</Label>
            <input ref={fileRef} type="file" required className="block w-full rounded border border-slate-300 px-2 py-1.5 text-xs" data-testid="doc-file" />
          </div>
          <DialogFooter>
            <Button variant="ghost" type="button" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={busy} className="btn-primary-blue" data-testid="doc-upload-submit">{busy ? "Uploading…" : "Upload"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
